/**
 * Configuration for parsing spreadsheet data
 */
export interface ParseSpreadsheetConfig<T = Record<string, unknown>> {
  /**
   * Function to find the header row index.
   * Return the index of the header row, or -1 if not found.
   * If not provided, defaults to row 0 (first row).
   *
   * @example
   * // Find first row that contains "name" and "email"
   * findHeaderRow: (rows) => rows.findIndex(row =>
   *   row.some(cell => cell.toLowerCase().includes('name')) &&
   *   row.some(cell => cell.toLowerCase().includes('email'))
   * )
   */
  findHeaderRow?: (rows: string[][]) => number

  /**
   * Function to transform header names into object keys.
   * Return false to skip the column entirely.
   * If not provided, defaults to using headers as-is.
   *
   * @example
   * // Convert to camelCase
   * mapHeader: (header) => toCamel(header)
   *
   * @example
   * // Skip columns that start with underscore
   * mapHeader: (header) => header.startsWith('_') ? false : header
   */
  mapHeader?: (header: string, columnIndex: number) => string | false

  /**
   * Function to transform cell values before adding to object.
   * Useful for parsing dates, numbers, or other data types.
   *
   * @example
   * transformValue: (value, key) => {
   *   if (key === 'age') return parseInt(value, 10)
   *   return value
   * }
   */
  transformValue?: (
    value: string,
    key: string,
    rowIndex: number,
    columnIndex: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => any

  /**
   * Skip rows where all cells are empty or whitespace.
   * Defaults to true.
   */
  skipEmptyRows?: boolean

  /**
   * Custom validation function for each row.
   * Return false to skip the row.
   *
   * @example
   * validateRow: (obj) => obj.email && obj.name
   */
  validateRow?: (row: T) => boolean
}

/**
 * Result of parsing spreadsheet data
 */
export interface ParseSpreadsheetResult<T> {
  /** Parsed data objects */
  data: T[]
  /** Header keys used for object properties */
  headers: string[]
  /** Index of the header row in the original data */
  headerRowIndex: number
  /** Number of rows skipped (empty or invalid) */
  skippedRows: number
}

/**
 * Parse spreadsheet data (array of string arrays) into typed objects.
 *
 * @param data - Array of string arrays (rows and columns)
 * @param config - Optional configuration for parsing behavior
 * @returns Parsed data with metadata
 *
 * @example
 * // Basic usage - first row as headers
 * const result = parseSpreadsheet([
 *   ['name', 'age', 'email'],
 *   ['John', '25', 'john@example.com'],
 *   ['Jane', '30', 'jane@example.com']
 * ])
 * // result.data = [{ name: 'John', age: '25', email: '...' }, ...]
 *
 * @example
 * // With camelCase conversion
 * const result = parseSpreadsheet(data, {
 *   mapHeader: (header) => toCamel(header.toLowerCase())
 * })
 *
 * @example
 * // Skip columns and find header row
 * const result = parseSpreadsheet(data, {
 *   findHeaderRow: (rows) => rows.findIndex(row =>
 *     row.some(cell => cell.toLowerCase() === 'name')
 *   ),
 *   mapHeader: (header) => header.startsWith('_') ? false : header
 * })
 */
export function parseSpreadsheet<T = Record<string, unknown>>(
  data: string[][],
  config: ParseSpreadsheetConfig<T> = {},
): ParseSpreadsheetResult<T> {
  const {
    findHeaderRow,
    mapHeader,
    transformValue,
    skipEmptyRows = true,
    validateRow,
  } = config

  if (!data || data.length === 0) {
    return {
      data: [],
      headers: [],
      headerRowIndex: -1,
      skippedRows: 0,
    }
  }

  // Find header row
  const headerRowIndex = findHeaderRow ? findHeaderRow(data) : 0

  if (headerRowIndex === -1 || headerRowIndex >= data.length) {
    throw new Error('Header row not found or invalid index')
  }

  // Get and process headers
  const rawHeaders = data[headerRowIndex]
  const headerMap: Array<{ originalIndex: number; key: string }> = []

  rawHeaders.forEach((header, index) => {
    const key = mapHeader ? mapHeader(header, index) : header
    if (key !== false) {
      headerMap.push({ originalIndex: index, key })
    }
  })

  if (headerMap.length === 0) {
    throw new Error('No valid headers found after mapping')
  }

  // Parse data rows
  const result: T[] = []
  let skippedRows = 0

  for (let i = headerRowIndex + 1; i < data.length; i++) {
    const row = data[i]

    // Skip empty rows if configured
    if (skipEmptyRows && isRowEmpty(row)) {
      skippedRows++
      continue
    }

    // Build object from row
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const obj: any = {}
    for (const { originalIndex, key } of headerMap) {
      const rawValue = row[originalIndex] || ''
      const value = transformValue
        ? transformValue(rawValue, key, i, originalIndex)
        : rawValue
      obj[key] = value
    }

    // Validate row if validator provided
    if (validateRow && !validateRow(obj as T)) {
      skippedRows++
      continue
    }

    result.push(obj as T)
  }

  return {
    data: result,
    headers: headerMap.map((h) => h.key),
    headerRowIndex,
    skippedRows,
  }
}

/**
 * Helper function to check if a row is empty
 */
function isRowEmpty(row: string[]): boolean {
  return !row || row.every((cell) => !cell || cell.trim() === '')
}
